import { useState, useEffect } from 'react';
import { HelpCircle, X, ChevronDown, Sparkles, CheckCircle2, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { PUBLIC_CHANGELOG as CHANGELOG } from '@/data/changelog';
import { cn } from '@/lib/utils';

interface ChangelogButtonProps {
  isCollapsed?: boolean;
  className?: string;
}

export function ChangelogButton({ isCollapsed = false, className }: ChangelogButtonProps) {
  const [open, setOpen] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [expandedVersion, setExpandedVersion] = useState<string | null>(CHANGELOG[0]?.version || null);
  const latestVersion = CHANGELOG[0]?.version;

  useEffect(() => {
    const lastSeen = localStorage.getItem('task-crm-last-seen-version');
    if (latestVersion && lastSeen !== latestVersion) {
      setHasNew(true);
    }
  }, [latestVersion]);

  const handleOpen = () => {
    setOpen(true);
    setHasNew(false);
    setExpandedVersion(CHANGELOG[0]?.version || null);
    localStorage.setItem('task-crm-last-seen-version', latestVersion || '');
  };

  const toggleVersion = (ver: string) => {
    setExpandedVersion(prev => prev === ver ? null : ver);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={cn(
          "flex items-center text-sm font-medium rounded-xl transition-colors relative cursor-pointer text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/70 hover:text-gray-900 dark:hover:text-white",
          isCollapsed ? "justify-center h-10 w-10 mx-auto p-0" : "px-3 py-2 w-full",
          className
        )}
        aria-label="Novidades do Task CRM"
        title="Novidades do Task CRM"
      >
        <div className="relative flex items-center justify-center shrink-0">
          <HelpCircle className="w-5 h-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200 transition-colors" />
          {hasNew && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-rose-500 rounded-full border-2 border-white dark:border-[#1A1D21] animate-pulse" />
          )}
        </div>
        {!isCollapsed && (
          <span className="truncate flex-1 ml-3 text-left">Novidades</span>
        )}
        {!isCollapsed && hasNew && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white shrink-0 ml-2 animate-pulse">
            NOVO
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="changelog-title"
              className="bg-white dark:bg-[#1A1D21] border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-blue-50 dark:bg-blue-950/80 text-blue-600 dark:text-blue-400">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 id="changelog-title" className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      Histórico de Atualizações
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Veja as últimas melhorias, novidades e correções do Task CRM
                    </p>
                  </div>
                </div>
                <button
                  aria-label="Fechar histórico de atualizações"
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Accordion Content */}
              <div className="overflow-y-auto p-5 space-y-3 flex-1">
                {CHANGELOG.map((v, idx) => {
                  const isExpanded = expandedVersion === v.version;
                  const isLatest = idx === 0;

                  return (
                    <div
                      key={v.version}
                      className={`border rounded-xl transition-all overflow-hidden ${
                        isExpanded
                          ? 'border-blue-200 dark:border-blue-900/50 bg-blue-50/20 dark:bg-blue-950/10 shadow-xs'
                          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/40 hover:border-gray-300 dark:hover:border-gray-700'
                      }`}
                    >
                      {/* Accordion Header */}
                      <button
                        type="button"
                        onClick={() => toggleVersion(v.version)}
                        aria-expanded={isExpanded}
                        className="w-full p-4 flex items-center justify-between gap-2 text-left cursor-pointer transition-colors"
                      >
                        <div className="flex min-w-0 flex-wrap items-center gap-3">
                          <span
                            className={`max-w-full break-words px-2.5 py-1 rounded-lg text-xs font-bold tracking-wide ${
                              isLatest
                                ? 'bg-blue-600 text-white shadow-xs'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {v.version}
                          </span>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            {new Date(v.date + 'T00:00:00').toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </span>
                          {v.status === 'preview' && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">EM TESTE · não publicado</span>
                          )}
                          {isLatest && (
                            <span className="text-[10px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-400">
                              Mais recente
                            </span>
                          )}
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                            isExpanded ? 'rotate-180 text-blue-600 dark:text-blue-400' : ''
                          }`}
                        />
                      </button>

                      {/* Accordion Body */}
                      <AnimatePresence initial={false}>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="p-4 pt-0 space-y-4 border-t border-gray-100 dark:border-gray-800/60 mt-1">
                              {v.novidades && v.novidades.length > 0 && (
                                <div>
                                  <h3 className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-2 flex items-center gap-1.5 pt-3">
                                    <Sparkles className="w-3.5 h-3.5" /> Novidades & Funcionalidades
                                  </h3>
                                  <ul className="space-y-1.5">
                                    {v.novidades.map((n, i) => (
                                      <li key={i} className="text-xs text-gray-700 dark:text-gray-300 flex items-start gap-2">
                                        <span className="text-emerald-500 mt-0.5">•</span>
                                        <span>{n}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {v.corrigidos && v.corrigidos.length > 0 && (
                                <div>
                                  <h3 className="text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1.5 pt-2">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Correções & Ajustes
                                  </h3>
                                  <ul className="space-y-1.5">
                                    {v.corrigidos.map((c, i) => (
                                      <li key={i} className="text-xs text-gray-700 dark:text-gray-300 flex items-start gap-2">
                                        <span className="text-amber-500 mt-0.5">•</span>
                                        <span>{c}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {v.melhorias && v.melhorias.length > 0 && (
                                <div>
                                  <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-2 flex items-center gap-1.5 pt-2">
                                    <Zap className="w-3.5 h-3.5" /> Melhorias & Performance
                                  </h3>
                                  <ul className="space-y-1.5">
                                    {v.melhorias.map((m, i) => (
                                      <li key={i} className="text-xs text-gray-700 dark:text-gray-300 flex items-start gap-2">
                                        <span className="text-blue-500 mt-0.5">•</span>
                                        <span>{m}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
